import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Sparkles, Brain, Zap, TrendingUp, ArrowRight, Monitor, Square, Smartphone, Maximize2, Minimize2, CheckCircle, Users, Clock, Briefcase, FileText, MessageSquare, ChevronRight, Eye, DollarSign, Calendar, BarChart3, Activity, Layers, Cpu } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const AdCreator = () => {
  const [activeTab, setActiveTab] = useState('16:9');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const ad169Ref = useRef<HTMLDivElement>(null);
  const ad11Ref = useRef<HTMLDivElement>(null);
  const ad916Ref = useRef<HTMLDivElement>(null);
  
  const downloadAd = async (ref, filename) => {
    try {
      toast.info('Genererer bilde...');
      const element = ref.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#0f172a',
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Bilde lastet ned!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Kunne ikke laste ned');
    }
  };

  const tabs = [
    { id: '16:9', label: '16:9 Landscape', icon: Monitor, ref: ad169Ref, filename: 'lexy-ad-landscape.png' },
    { id: '1:1', label: '1:1 Square', icon: Square, ref: ad11Ref, filename: 'lexy-ad-square.png' },
    { id: '9:16', label: '9:16 Story', icon: Smartphone, ref: ad916Ref, filename: 'lexy-ad-story.png' }
  ];

  const currentTab = tabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-3">Lexy Annonsebilder</h1>
          <p className="text-slate-300 text-lg">100% Responsive - Premium Design</p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg'
                  : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800 border border-slate-700'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.id}</span>
            </button>
          ))}
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <Button
            onClick={() => setIsFullScreen(!isFullScreen)}
            className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-3"
          >
            {isFullScreen ? <Minimize2 className="w-5 h-5 mr-2" /> : <Maximize2 className="w-5 h-5 mr-2" />}
            {isFullScreen ? 'Normal' : 'Full visning'}
          </Button>
          <Button
            onClick={() => downloadAd(currentTab.ref, currentTab.filename)}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-6 py-3 shadow-lg"
          >
            <Download className="w-5 h-5 mr-2" />
            Last ned
          </Button>
        </div>
        
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-slate-900/50 rounded-2xl border border-slate-700/50 p-4 lg:p-8 shadow-2xl"
          >
            <div className={`mx-auto ${
              isFullScreen ? 'w-full' : 
              activeTab === '16:9' ? 'max-w-6xl' : 
              activeTab === '1:1' ? 'max-w-3xl' : 
              'max-w-lg'
            }`}>
              <div className={`w-full rounded-xl overflow-hidden shadow-2xl ${
                activeTab === '16:9' ? 'aspect-video' : 
                activeTab === '1:1' ? 'aspect-square' : 
                'aspect-[9/16]'
              }`}>
                {activeTab === '16:9' && <AdLandscape ref={ad169Ref} />}
                {activeTab === '1:1' && <AdSquare ref={ad11Ref} />}
                {activeTab === '9:16' && <AdStory ref={ad916Ref} />}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        <p className="text-center text-slate-400 text-sm mt-6">
          ✨ Alt skalerer perfekt innenfor rammene
        </p>
      </div>
    </div>
  );
};

// 16:9 - FULLY RESPONSIVE
const AdLandscape = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div ref={ref} className="w-full h-full bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 relative overflow-hidden">
    {/* Ambient background */}
    <div className="absolute inset-0">
      <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-gradient-to-br from-indigo-500/30 to-purple-500/30 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-[50%] h-[50%] bg-gradient-to-tr from-blue-500/20 to-violet-500/20 blur-[100px] rounded-full"></div>
    </div>
    
    <div className="relative h-full flex items-center justify-between px-[6%] py-[5%] gap-[4%]">
      {/* LEFT: Text - 42% width */}
      <div className="w-[42%] flex flex-col justify-center space-y-[3vh]">
        <div>
          <div className="inline-flex items-center gap-[0.5vw] px-[1.5vw] py-[0.8vh] rounded-full bg-white/5 backdrop-blur-xl border border-white/10 mb-[2vh]">
            <div className="w-[0.5vw] h-[0.5vw] bg-emerald-400 rounded-full animate-pulse"></div>
            <span className="text-emerald-300 font-semibold" style={{ fontSize: 'clamp(0.6rem, 0.8vw, 1rem)' }}>Nå tilgjengelig</span>
          </div>
          
          <h1 className="font-black text-white leading-[1.1] tracking-tight mb-[1.5vh]" style={{ fontSize: 'clamp(2rem, 4vw, 5rem)' }}>
            AI som
          </h1>
          <h2 className="font-black bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent leading-[1.1] mb-[2vh]" style={{ fontSize: 'clamp(2rem, 4vw, 5rem)' }}>
            jobber for deg
          </h2>
          
          <p className="text-slate-300 leading-relaxed" style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1.5rem)' }}>
            Lexy tar seg av 90% av arbeidet.
            <br />
            Du fokuserer på det som betyr noe.
          </p>
        </div>
        
        <div className="space-y-[1.5vh]">
          {[
            { icon: Zap, title: '12t → 12 min', desc: 'AI-drevet' },
            { icon: Brain, title: '94% auto', desc: 'Dokumenter' },
            { icon: TrendingUp, title: 'Verdibasert', desc: 'Prising' }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-[1vw] group">
              <div className="rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 backdrop-blur-xl border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ width: 'clamp(2rem, 2.5vw, 3rem)', height: 'clamp(2rem, 2.5vw, 3rem)' }}>
                <item.icon className="text-indigo-300" style={{ width: 'clamp(1rem, 1.3vw, 1.5rem)', height: 'clamp(1rem, 1.3vw, 1.5rem)' }} />
              </div>
              <div>
                <div className="text-white font-bold" style={{ fontSize: 'clamp(0.8rem, 1vw, 1.2rem)' }}>{item.title}</div>
                <div className="text-slate-400" style={{ fontSize: 'clamp(0.6rem, 0.8vw, 1rem)' }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
        
        <button type="button" className="group inline-flex items-center justify-center gap-[1vw] px-[2vw] py-[1.5vh] bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl text-white font-bold shadow-2xl shadow-indigo-500/50 hover:scale-105 transition-all self-start" style={{ fontSize: 'clamp(0.8rem, 1.1vw, 1.3rem)' }}>
          <span>Kom i gang</span>
          <ArrowRight style={{ width: 'clamp(1rem, 1.3vw, 1.5rem)', height: 'clamp(1rem, 1.3vw, 1.5rem)' }} />
        </button>
      </div>
      
      {/* RIGHT: Mockup - 52% width */}
      <div className="w-[52%] relative">
        {/* Main dashboard */}
        <div className="relative z-10">
          <div className="bg-slate-900/90 backdrop-blur-2xl rounded-[1vw] border border-white/10 shadow-2xl shadow-black/50 overflow-hidden">
            {/* Top bar */}
            <div className="bg-slate-950/50 backdrop-blur-xl border-b border-white/5 px-[3%] py-[2%] flex items-center justify-between">
              <div className="flex items-center gap-[0.3vw]">
                <div className="rounded-full bg-red-500/80" style={{ width: 'clamp(0.4rem, 0.6vw, 0.8rem)', height: 'clamp(0.4rem, 0.6vw, 0.8rem)' }}></div>
                <div className="rounded-full bg-yellow-500/80" style={{ width: 'clamp(0.4rem, 0.6vw, 0.8rem)', height: 'clamp(0.4rem, 0.6vw, 0.8rem)' }}></div>
                <div className="rounded-full bg-green-500/80" style={{ width: 'clamp(0.4rem, 0.6vw, 0.8rem)', height: 'clamp(0.4rem, 0.6vw, 0.8rem)' }}></div>
              </div>
              <div className="flex items-center gap-[0.5vw]">
                <div className="bg-white rounded-lg flex items-center justify-center" style={{ width: 'clamp(1rem, 1.3vw, 1.5rem)', height: 'clamp(1rem, 1.3vw, 1.5rem)' }}>
                  <span className="font-bold text-slate-900" style={{ fontSize: 'clamp(0.5rem, 0.7vw, 0.9rem)' }}>L</span>
                </div>
                <span className="text-white font-semibold" style={{ fontSize: 'clamp(0.6rem, 0.8vw, 1rem)' }}>Lexy</span>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-[4%] space-y-[2vh]">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-[2%]">
                {[
                  { icon: Briefcase, value: '24', label: 'Aktive' },
                  { icon: CheckCircle, value: '156', label: 'Fullført' },
                  { icon: Clock, value: '180t', label: 'Spart' },
                  { icon: Brain, value: '94%', label: 'AI' }
                ].map((stat, i) => (
                  <div key={i} className="bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-lg p-[8%]">
                    <stat.icon className="text-slate-500 mb-[5%]" style={{ width: 'clamp(0.8rem, 1vw, 1.2rem)', height: 'clamp(0.8rem, 1vw, 1.2rem)' }} />
                    <div className="text-white font-bold" style={{ fontSize: 'clamp(1rem, 1.3vw, 1.6rem)' }}>{stat.value}</div>
                    <div className="text-slate-400" style={{ fontSize: 'clamp(0.5rem, 0.6vw, 0.8rem)' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
              
              {/* Cases */}
              <div className="space-y-[1vh]">
                {[
                  { title: 'Avtalegjennomgang', client: 'TechCorp', progress: 75 },
                  { title: 'Klientopptak', client: 'Nordland', progress: 100 }
                ].map((item, i) => (
                  <div key={i} className="bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-lg p-[3%]">
                    <div className="flex items-center justify-between mb-[1vh]">
                      <div className="flex-1">
                        <div className="flex items-center gap-[0.5vw] mb-[0.3vh]">
                          <span className="text-white font-semibold" style={{ fontSize: 'clamp(0.6rem, 0.8vw, 1rem)' }}>{item.title}</span>
                          <div className="flex items-center gap-[0.3vw] px-[0.5vw] py-[0.2vh] bg-slate-500/20 rounded-full">
                            <Sparkles className="text-indigo-300" style={{ width: 'clamp(0.5rem, 0.6vw, 0.8rem)', height: 'clamp(0.5rem, 0.6vw, 0.8rem)' }} />
                          </div>
                        </div>
                        <div className="text-slate-400" style={{ fontSize: 'clamp(0.5rem, 0.6vw, 0.8rem)' }}>{item.client}</div>
                      </div>
                    </div>
                    <div className="w-full bg-slate-900/50 rounded-full overflow-hidden" style={{ height: 'clamp(0.3rem, 0.4vw, 0.5rem)' }}>
                      <div className="h-full bg-gradient-to-r from-violet-500 to-purple-600" style={{ width: `${item.progress}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Floating AI card */}
        <div className="absolute top-[-5%] right-[-8%] z-20 w-[42%] transform rotate-2">
          <div className="bg-gradient-to-br from-indigo-500/90 to-purple-600/90 backdrop-blur-2xl rounded-xl border border-white/20 shadow-2xl p-[6%]">
            <div className="flex items-center gap-[1vw] mb-[1vh]">
              <div className="rounded-xl bg-white/20 flex items-center justify-center" style={{ width: 'clamp(1.5rem, 2vw, 2.5rem)', height: 'clamp(1.5rem, 2vw, 2.5rem)' }}>
                <Cpu className="text-white" style={{ width: 'clamp(0.8rem, 1vw, 1.2rem)', height: 'clamp(0.8rem, 1vw, 1.2rem)' }} />
              </div>
              <div>
                <div className="text-white font-bold" style={{ fontSize: 'clamp(0.6rem, 0.8vw, 1rem)' }}>AI Aktivitet</div>
                <div className="text-indigo-100" style={{ fontSize: 'clamp(0.5rem, 0.6vw, 0.8rem)' }}>Sanntid</div>
              </div>
            </div>
            <div className="space-y-[0.5vh]">
              {['Analyserer...', 'Genererer...'].map((text, i) => (
                <div key={i} className="flex items-center gap-[0.5vw]">
                  <div className="bg-emerald-300 rounded-full animate-pulse" style={{ width: 'clamp(0.3rem, 0.4vw, 0.5rem)', height: 'clamp(0.3rem, 0.4vw, 0.5rem)' }}></div>
                  <span className="text-white/90" style={{ fontSize: 'clamp(0.5rem, 0.7vw, 0.9rem)' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Floating stat */}
        <div className="absolute bottom-[-3%] left-[-6%] z-20 transform -rotate-3">
          <div className="bg-slate-900/95 backdrop-blur-2xl rounded-xl border border-emerald-500/30 shadow-2xl p-[6%]">
            <div className="flex items-center gap-[0.5vw] mb-[0.5vh]">
              <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center" style={{ width: 'clamp(1.5rem, 2vw, 2.5rem)', height: 'clamp(1.5rem, 2vw, 2.5rem)' }}>
                <TrendingUp className="text-white" style={{ width: 'clamp(0.8rem, 1vw, 1.2rem)', height: 'clamp(0.8rem, 1vw, 1.2rem)' }} />
              </div>
              <span className="text-emerald-400 font-semibold" style={{ fontSize: 'clamp(0.6rem, 0.8vw, 1rem)' }}>+142%</span>
            </div>
            <div className="text-white font-bold" style={{ fontSize: 'clamp(0.8rem, 1vw, 1.2rem)' }}>Produktivitet</div>
            <div className="text-slate-400" style={{ fontSize: 'clamp(0.5rem, 0.6vw, 0.8rem)' }}>vs. tradisjonelt</div>
          </div>
        </div>
      </div>
    </div>
  </div>
));

// 1:1 & 9:16 remain the same structure with clamp() sizing
const AdSquare = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div ref={ref} className="w-full h-full bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 relative overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute top-0 right-0 w-[70%] h-[70%] bg-gradient-to-br from-purple-500/30 to-pink-500/30 blur-[100px]"></div>
    </div>
    
    <div className="relative h-full flex flex-col p-[8%]">
      <div className="mb-auto">
        <div className="flex items-center gap-[1vw] mb-[3vh]">
          <div className="bg-white rounded-2xl flex items-center justify-center shadow-2xl" style={{ width: 'clamp(2rem, 3vw, 4rem)', height: 'clamp(2rem, 3vw, 4rem)' }}>
            <span className="font-black text-slate-900" style={{ fontSize: 'clamp(1rem, 1.5vw, 2rem)' }}>L</span>
          </div>
          <div>
            <div className="text-white font-black" style={{ fontSize: 'clamp(1rem, 1.5vw, 2rem)' }}>Lexy</div>
            <div className="text-slate-400" style={{ fontSize: 'clamp(0.6rem, 0.8vw, 1rem)' }}>AI-first</div>
          </div>
        </div>
        
        <h1 className="text-white font-black leading-tight mb-[2vh]" style={{ fontSize: 'clamp(2rem, 3.5vw, 4rem)' }}>
          AI som
          <br />
          <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">revolusjonerer</span>
          <br />
          advokatarbeid
        </h1>
        
        <p className="text-slate-300 leading-relaxed mb-[3vh]" style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1.5rem)' }}>
          Fra 12 timer til 12 minutter.
        </p>
      </div>
      
      <div className="flex-1 flex items-center justify-center my-[3vh]">
        <div className="w-[65%]">
          <div className="bg-slate-900/95 backdrop-blur-2xl rounded-[2rem] border-[0.5vw] border-slate-800 shadow-2xl overflow-hidden">
            <div style={{ aspectRatio: '9/19.5' }} className="bg-gradient-to-b from-slate-950 to-slate-900 p-[6%] flex flex-col">
              <div className="w-1/3 h-[1.5vh] bg-slate-950 rounded-b-3xl mx-auto mb-[2vh]"></div>
              <div className="grid grid-cols-2 gap-[2%] flex-1">
                {[{ icon: Briefcase, value: '24' }, { icon: Brain, value: '94%' }].map((stat, i) => (
                  <div key={i} className="bg-slate-800/50 border border-white/10 rounded-2xl p-[8%] flex flex-col justify-center">
                    <stat.icon className="text-slate-500 mb-[1vh]" style={{ width: 'clamp(1rem, 1.5vw, 2rem)', height: 'clamp(1rem, 1.5vw, 2rem)' }} />
                    <div className="text-white font-black" style={{ fontSize: 'clamp(1.5rem, 2vw, 2.5rem)' }}>{stat.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <button type="button" className="w-full py-[2vh] bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl text-white font-black shadow-2xl flex items-center justify-center gap-[1vw]" style={{ fontSize: 'clamp(1rem, 1.5vw, 2rem)' }}>
        <span>Kom i gang</span>
        <ArrowRight style={{ width: 'clamp(1rem, 1.5vw, 2rem)', height: 'clamp(1rem, 1.5vw, 2rem)' }} />
      </button>
    </div>
  </div>
));

const AdStory = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div ref={ref} className="w-full h-full bg-gradient-to-b from-slate-950 via-indigo-950 to-purple-950 relative overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute top-0 w-full h-1/3 bg-gradient-to-b from-indigo-500/20 blur-3xl"></div>
    </div>
    
    <div className="relative h-full flex flex-col p-[10%]">
      <div className="flex items-center gap-[2vw] mb-[8vh] pt-[4vh]">
        <div className="bg-white rounded-2xl flex items-center justify-center shadow-2xl" style={{ width: 'clamp(2rem, 4vw, 5rem)', height: 'clamp(2rem, 4vw, 5rem)' }}>
          <span className="font-black text-slate-900" style={{ fontSize: 'clamp(1.5rem, 3vw, 4rem)' }}>L</span>
        </div>
        <div>
          <div className="text-white font-black" style={{ fontSize: 'clamp(1.5rem, 3vw, 4rem)' }}>Lexy</div>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col justify-center text-center">
        <h1 className="text-white font-black leading-tight mb-[4vh]" style={{ fontSize: 'clamp(3rem, 6vw, 7rem)' }}>
          AI som
          <br />
          <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">jobber</span>
          <br />
          for deg
        </h1>
        
        <div className="flex justify-center mb-[6vh]">
          <div className="w-[75%]">
            <div className="bg-slate-900/95 rounded-[3rem] border-[1vw] border-slate-800 shadow-2xl overflow-hidden">
              <div style={{ aspectRatio: '9/19.5' }} className="bg-gradient-to-b from-slate-950 to-slate-900 p-[10%] flex items-center justify-center">
                <div className="space-y-[2vh] w-full">
                  {[{ icon: Brain, value: '94%', label: 'AI Score' }].map((stat, i) => (
                    <div key={i} className="bg-slate-800/50 border border-white/10 rounded-3xl p-[8%]">
                      <stat.icon className="text-slate-500 mb-[2vh] mx-auto" style={{ width: 'clamp(2rem, 4vw, 5rem)', height: 'clamp(2rem, 4vw, 5rem)' }} />
                      <div className="text-white font-black" style={{ fontSize: 'clamp(3rem, 5vw, 6rem)' }}>{stat.value}</div>
                      <div className="text-slate-400" style={{ fontSize: 'clamp(0.8rem, 1.5vw, 2rem)' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <button type="button" className="w-full py-[3vh] bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl text-white font-black shadow-2xl flex items-center justify-center gap-[2vw]" style={{ fontSize: 'clamp(1.5rem, 3vw, 3.5rem)' }}>
        <span>Kom i gang</span>
        <ArrowRight style={{ width: 'clamp(1.5rem, 3vw, 4rem)', height: 'clamp(1.5rem, 3vw, 4rem)' }} />
      </button>
    </div>
  </div>
));

export default AdCreator;
